import React, { useState, useEffect } from 'react';
import SelectField from '../common/SelectField.js';
import { useNavigate, useParams } from 'react-router-dom';
import Header from '../common/Header.js';
import Sidebar from '../common/Sidebar.js';
import Alert from '../common/Alert.js';
import inventorySKUService from '../../services/InventorySKUService.js';
import { Package } from "lucide-react";
import { useTranslation } from 'react-i18next';


const EditInventorySKU = () => {
  const { skuId } = useParams();
  const [formData, setFormData] = useState({
    sku_code: '',
    sku_name: '',
    quantity: '',
    unit_price: '',
    variant_type: '',
    variant_value: '',
    bin_location: '',
    notes: '',
    is_active: 'true'
  });
  
  const [skuImage, setSkuImage] = useState(null);
  const [skuImagePreview, setSkuImagePreview] = useState(null);
  const [existingSKUImage, setExistingSKUImage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [inventoryInfo, setInventoryInfo] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchSKU();
  }, [skuId]);

  const fetchSKU = async () => {
    try {
      const response = await inventorySKUService.getById(skuId);
      const skuData = response.data;
      setFormData({
        sku_code: skuData.sku_code || '',
        sku_name: skuData.sku_name || '',
        quantity: skuData.quantity || '',
        unit_price: skuData.unit_price || '',
        variant_type: skuData.variant_type || '',
        variant_value: skuData.variant_value || '',
        bin_location: skuData.bin_location || '',
        notes: skuData.notes || '',
        is_active: skuData.is_active ? 'true' : 'false'
      });
      
      if (skuData.inventory_info) {
        setInventoryInfo(skuData.inventory_info);
      }
      
      if (skuData.sku_image_base64) {
        setExistingSKUImage(`data:image/jpeg;base64,${skuData.sku_image_base64}`);
      }
    } catch (err) {
      Alert.error('Failed to load SKU');
      console.error(err);
      navigate('/inventory');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSKUImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        Alert.error('SKU image must be less than 5MB');
        return;
      }
      setSkuImage(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setSkuImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const removeSKUImage = () => {
    setSkuImage(null);
    setSkuImagePreview(null);
    const fileInput = document.getElementById('sku-image-input');
    if (fileInput) fileInput.value = '';
  };

  const removeExistingSKUImage = () => {
    setExistingSKUImage(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const response = await inventorySKUService.update(skuId, formData, skuImage);
      Alert.success('SKU updated successfully!');
      
      if (response.inventory_updated) {
        Alert.info(`Inventory updated: ${response.inventory_updated.total_quantity} units total`);
      }
      
      setTimeout(() => {
        if (inventoryInfo?.inventory_id) {
          navigate(`/inventory/view/${inventoryInfo.inventory_id}`);
        } else {
          navigate('/inventory');
        }
      }, 1500);
    } catch (err) {
      Alert.error(err.response?.data?.error || 'Failed to update SKU');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="max-w-[1850px] mx-auto">
        <Header />
        <div className="flex">
          <Sidebar />
          <div className="flex bg-light w-full mt-16 sm:mt-0">
            <div className="flex-1 flex flex-col overflow-hidden">
              <main className="flex-1 flex items-center justify-center">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mx-auto mb-4"></div>
                  <p className="text-sm sm:text-base text-gray-600">Loading SKU...</p>
                </div>
              </main>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const displayImage = skuImagePreview || existingSKUImage;

  return (
    <div className="max-w-[1850px] mx-auto">
      <Header />
      <div className="flex">
        <Sidebar />
        <div className="flex bg-light w-full mt-16 sm:mt-0">
          <div className="flex-1 flex flex-col overflow-hidden">
            <main className="flex-1 overflow-auto p-3 sm:p-6">
              <div className="max-w-7xl mx-auto">
                {/* Header */}
                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 sm:gap-4 mb-4 sm:mb-4">
                  <h1 className="text-2xl sm:text-xl font-bold text-secondary flex items-center gap-2 sm:gap-3">
                    <Package className="w-5 h-5 text-buttonBg" /> Edit SKU
                  </h1>
                </div>

                {/* Parent Inventory Info */}
                {inventoryInfo && (
                  <div className="bg-blue-50 border border-blue-200 rounded-sm p-4 mb-4">
                    <h3 className="text-sm font-semibold text-blue-900 mb-2">Parent Inventory Item</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 text-sm">
                      <div>
                        <span className="text-blue-700">Item:</span>
                        <span className="ml-2 font-medium text-blue-900">{inventoryInfo.item_name}</span>
                      </div>
                      <div>
                        <span className="text-blue-700">Category:</span>
                        <span className="ml-2 font-medium text-blue-900">{inventoryInfo.category}</span>
                      </div>
                      <div>
                        <span className="text-blue-700">Location:</span>
                        <span className="ml-2 font-medium text-blue-900">{inventoryInfo.warehouse_location}</span>
                      </div>
                    </div>
                  </div>
                )}

                {/* Form */}
                <form onSubmit={handleSubmit} className="bg-white rounded-sm shadow-sm p-4 sm:p-8 space-y-6 sm:space-y-8">
                  {/* SKU Image Section */}
                  <div>
                    <h3 className="text-base sm:text-md font-semibold text-tertiary mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">SKU Image</h3>
                    <div className="flex flex-col sm:flex-row items-center sm:items-center gap-4 sm:gap-6">
                      <div className="w-20 h-20 sm:w-24 sm:h-24 border-2 border-gray-300 
                                      overflow-hidden bg-gray-100 flex items-center justify-center flex-shrink-0 rounded-md">
                        {displayImage ? (
                          <img 
                            src={displayImage} 
                            alt="SKU" 
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              e.target.onerror = null;
                              e.target.innerHTML = '<Package className="w-10 h-10 text-gray-400" />';
                            }}
                          />
                        ) : (
                          <Package className="w-10 h-10 text-gray-400" />
                        )}
                      </div>

                      <div className="flex-1 w-full">
                        <label className="block text-xs sm:text-sm font-medium text-gray-900 mb-1 sm:mb-2">
                          {existingSKUImage && !skuImagePreview ? 'Update SKU Image' : 'Upload SKU Image'}
                        </label>
                        <input
                          id="sku-image-input"
                          type="file"
                          accept="image/*"
                          onChange={handleSKUImageChange}
                          className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base"
                        />
                        <p className="text-xs text-gray-600 mt-1">Accepted formats: JPG, PNG, GIF, WebP (Max 5MB)</p>
                        {skuImagePreview && (
                          <button
                            type="button"
                            onClick={removeSKUImage}
                            className="mt-2 text-xs sm:text-sm text-red-600 hover:text-red-700"
                          >
                            Remove New Image
                          </button>
                        )}
                        {existingSKUImage && !skuImagePreview && (
                          <button
                            type="button"
                            onClick={removeExistingSKUImage}
                            className="mt-2 text-xs sm:text-sm text-red-600 hover:text-red-700"
                          >
                            Remove Existing Image
                          </button>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Section 1: Basic Information */}
                  <div>
                    <h3 className="text-base sm:text-md font-semibold text-tertiary mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">Basic Information</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                          SKU Code <span className="text-danger font-bold">*</span>
                        </label>
                        <input
                          type="text"
                          name="sku_code"
                          value={formData.sku_code}
                          onChange={handleChange}
                          className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base"
                          required
                        />
                      </div>

                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                          SKU Name <span className="text-danger font-bold">*</span>
                        </label>
                        <input
                          type="text"
                          name="sku_name"
                          value={formData.sku_name}
                          onChange={handleChange}
                          className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base"
                          required
                        />
                      </div>

                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                          Variant Type
                        </label>
                        <input
                          type="text"
                          name="variant_type"
                          value={formData.variant_type}
                          onChange={handleChange}
                          placeholder="e.g., Size, Color, Model"
                          className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base"
                        />
                      </div>

                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                          Variant Value
                        </label>
                        <input
                          type="text"
                          name="variant_value"
                          value={formData.variant_value}
                          onChange={handleChange}
                          placeholder="e.g., Large, Red, V2"
                          className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Section 2: Quantity and Pricing */}
                  <div>
                    <h3 className="text-base sm:text-md font-semibold text-tertiary mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">Quantity and Pricing</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                          Quantity <span className="text-danger font-bold">*</span>
                        </label>
                        <input
                          type="number"
                          name="quantity"
                          value={formData.quantity}
                          onChange={handleChange}
                          min="0"
                          className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base"
                          required
                        />
                        <p className="text-xs text-gray-600 mt-1">Parent inventory will be updated accordingly</p>
                      </div>

                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                          Unit Price (₹) <span className="text-danger font-bold">*</span>
                        </label>
                        <input
                          type="number"
                          name="unit_price"
                          value={formData.unit_price}
                          onChange={handleChange}
                          min="0"
                          step="0.01"
                          className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base"
                          required
                        />
                        <p className="text-xs text-gray-600 mt-1">Parent inventory price will be recalculated</p>
                      </div>
                    </div>
                  </div>

                  {/* Section 3: Storage Location */}
                  <div>
                    <h3 className="text-base sm:text-md font-semibold text-tertiary mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">Storage Location</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                          Bin Location
                        </label>
                        <input
                          type="text"
                          name="bin_location"
                          value={formData.bin_location}
                          onChange={handleChange}
                          placeholder="e.g., Shelf A3, Bin 12"
                          className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base"
                        />
                      </div>

                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                          Status
                        </label>
                        <SelectField variant="form"
                          name="is_active"
                          value={formData.is_active}
                          onChange={handleChange}
                          className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base"
                        >
                          <option value="true">Active</option>
                          <option value="false">Inactive</option>
                        </SelectField>
                      </div>
                    </div>
                  </div>

                  {/* Section 4: Additional Information */}
                  <div>
                    <h3 className="text-base sm:text-md font-semibold text-tertiary mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">Additional Information</h3>
                    <div className="grid grid-cols-1 gap-4 sm:gap-6">
                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                          Notes
                        </label>
                        <textarea
                          name="notes"
                          value={formData.notes}
                          onChange={handleChange}
                          placeholder="Enter any additional notes"
                          rows="3"
                          className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 pt-4 sm:pt-6 border-t">
                    <button
                      type="submit"
                      disabled={submitting}
                      className="group flex items-center justify-center gap-2 px-3 sm:px-3 py-2.5 sm:py-2 text-xs sm:text-sm text-buttonTxt bg-buttonBg hover:text-buttonBg hover:bg-buttonTxt border 
border-current rounded-sm  transition-all duration-300 font-medium whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {submitting ? 'Updating...' : 'Update SKU'}
                    </button>
                    <button
                      type="button"
                      onClick={() => inventoryInfo?.inventory_id ? navigate(`/inventory/view/${inventoryInfo.inventory_id}`) : navigate('/inventory')}
                      className="group flex items-center justify-center gap-2 px-3 sm:px-3 py-2.5 sm:py-2 text-xs sm:text-sm text-buttonBg bg-buttonTxt hover:text-buttonTxt 
hover:bg-buttonBg border border-current rounded-sm  transition-all duration-300 font-medium whitespace-nowrap"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            </main>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditInventorySKU;